import React from 'react';
import './rebate-document.scss';

export type RebateDocumentLanguage = 'en' | 'ar';
export type RebateDocumentType = 'invoice' | 'incentive' | 'cancellation';

export interface RebateDocumentViewProps {
  language?: RebateDocumentLanguage;
  docType: RebateDocumentType;
  referenceNo: string;
  date: string;
  status?: string;
  recipientName: string;
  beneficiaryName: string;
  accountNumber: string;
  totalDeals?: number;
  amount: number;
  currency: string;
  month: string;
  captureRef?: React.Ref<HTMLDivElement>;
}

export const COMPANY = {
  logo: `${window.location.origin}/content/images/img.png`,
  address: 'Mirqab Area, Al-Soor St., Jasim Tower, Floor 7, Office B&C, State of Kuwait, Kuwait City',
  website: 'Alshmokhholding.com',
  email: 'info@sh-global.com',
  phone: '+965 2296 0510',
};

export const SHMOKH_NAME: Record<RebateDocumentLanguage, string> = {
  en: 'Al Shmokh',
  ar: 'الشموخ',
};

export const THIRD_PARTY_NAME: Record<RebateDocumentLanguage, string> = {
  en: 'Third Party',
  ar: 'الطرف الثالث',
};

export const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const LABELS: Record<RebateDocumentLanguage, Record<string, any>> = {
  en: {
    titles: {
      invoice: 'Marketing Incentive - invoice',
      incentive: 'Marketing Incentive',
      cancellation: 'Cancellation Form',
    },
    body: {
      invoice:
        'With reference to the above subject, and in accordance with the contract concluded between us, the below amount will be paid as marketing incentives to our company.',
      incentive:
        'With reference to the above subject, and in accordance with the contract concluded between us, the below amount will be paid as marketing incentives to the bank.',
      cancellation:
        'With reference to the above subject, and in accordance with the contract concluded between us, the below amount will be paid as Cancellation fees to our company.',
    },
    referenceNo: 'Reference No.',
    date: 'Date',
    status: 'Status',
    statusIssued: 'Issued',
    recipient: 'Recipient',
    paymentDetails: 'Payment Details',
    beneficiary: 'Beneficiary',
    accountNumber: 'Account Number',
    totalDeals: 'Total Deals',
    amount: 'Amount',
    period: 'Period',
    companySignature: 'Company signature',
  },
  ar: {
    titles: {
      invoice: 'الحوافز التسويقية - فاتورة',
      incentive: 'الحوافز التسويقية',
      cancellation: 'نموذج الغاء المعاملة',
    },
    body: {
      invoice: 'بالإشارة للموضوع أعلاه, وعلى العقد المبرم بيننا سوف يتم دفع المبلغ أدناه كحوافز تسويقية لشركتنا.',
      incentive: 'بالإشارة للموضوع أعلاه, وعلى العقد المبرم بيننا سوف يتم دفع المبلغ أدناه كحوافز تسويقية للبنك.',
      cancellation: 'بالإشارة للموضوع أعلاه, وعلى العقد المبرم بيننا سوف يتم دفع المبلغ أدناه كرسوم إلغاء لصالح شركتنا.',
    },
    referenceNo: 'المرجع',
    date: 'التاريخ',
    status: 'الحالة',
    statusIssued: 'صادر',
    recipient: 'الجهة',
    paymentDetails: 'تفاصيل الدفع',
    beneficiary: 'المستفيد',
    accountNumber: 'رقم الحساب',
    totalDeals: 'إجمالي عدد المعاملات',
    amount: 'المبلغ',
    period: 'الاستحقاق',
    companySignature: 'ختم الشركة',
  },
};

export const formatAmount = (amount: number, currency: string) => {
  const formatted = (amount ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  return `${formatted} ${currency || ''}`.trim();
};

export const RebateDocumentView = (props: RebateDocumentViewProps) => {
  const {
    language = 'en',
    docType,
    referenceNo,
    date,
    status,
    recipientName,
    beneficiaryName,
    accountNumber,
    totalDeals,
    amount,
    currency,
    month,
    captureRef,
  } = props;

  const labels = LABELS[language];
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div
      ref={captureRef}
      id="rebateDocumentToPrint"
      className="rebate-document"
      dir={dir}
      lang={language}
    >
      <div className="rebate-document__header" dir="ltr">
        <img className="rebate-document__logo" src={COMPANY.logo} alt="Al Shmokh Holding" />
      </div>

      <div className="rebate-document__body">
        <h1 className="rebate-document__title">{labels.titles[docType]}</h1>

        <div className="rebate-document__meta">
          <div className="rebate-document__meta-row">
            <span className="rebate-document__meta-label">{labels.referenceNo}:</span>
            <span className="rebate-document__meta-value">{referenceNo}</span>
          </div>
          <div className="rebate-document__meta-row">
            <span className="rebate-document__meta-label">{labels.date}:</span>
            <span className="rebate-document__meta-value">{date}</span>
          </div>
          <div className="rebate-document__meta-row">
            <span className="rebate-document__meta-label">{labels.status}:</span>
            <span className="rebate-document__meta-value">{status || labels.statusIssued}</span>
          </div>
          <div className="rebate-document__meta-row">
            <span className="rebate-document__meta-label">{labels.recipient}:</span>
            <span className="rebate-document__meta-value">{recipientName}</span>
          </div>
        </div>

        <p className="rebate-document__paragraph">{labels.body[docType]}</p>

        <h2 className="rebate-document__section-title">{labels.paymentDetails}</h2>

        <table className="rebate-document__table">
          <thead>
            <tr>
              <th>{labels.beneficiary}</th>
              <th>{labels.accountNumber}</th>
              {docType === 'cancellation' && <th>{labels.totalDeals}</th>}
              <th>{labels.amount}</th>
              <th>{labels.period}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{beneficiaryName}</td>
              <td>{accountNumber}</td>
              {docType === 'cancellation' && <td>{totalDeals ?? 0}</td>}
              <td>{formatAmount(amount, currency)}</td>
              <td>{month}</td>
            </tr>
          </tbody>
        </table>

        <p className="rebate-document__signature">{labels.companySignature}</p>
        <img
          className="rebate-document__signature-img"
          src="content/images/signature.png"
          alt={labels.companySignature}
        />
      </div>

      <div className="rebate-document__footer" dir="ltr">
        <div className="rebate-document__footer-rule" />
        <p className="rebate-document__footer-line">{COMPANY.address}</p>
        <p className="rebate-document__footer-line">
          {COMPANY.website} <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
        </p>
        <p className="rebate-document__footer-line">{COMPANY.phone}</p>
      </div>
    </div>
  );
};

export default RebateDocumentView;
